import type { TFunction } from 'i18next';

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;
const FALLBACK_MINUTE_TEXT = 'duration.minute_one';

/**
 * Formats duration parts into a localized string
 */
const formatDurationParts = (parts: string[], t: TFunction): string => {
    if (parts.length === 0) {
        return t(FALLBACK_MINUTE_TEXT, { count: 0 });
    }

    if (parts.length === 1) {
        return parts[0] || t(FALLBACK_MINUTE_TEXT, { count: 0 });
    }

    if (parts.length === 2) {
        return `${parts[0]} ${t('duration.and')} ${parts[1]}`;
    }

    // For 3+ parts, join all but last with commas, then add "and" before last
    const lastPart = parts[parts.length - 1];
    const otherParts = parts.slice(0, -1);

    return `${otherParts.join(', ')} ${t('duration.and')} ${lastPart}`;
};

/**
 * Converts minutes to a human-readable duration string using i18n translations
 *
 * @param minutes - Number of minutes to convert (e.g., 272.6895)
 * @param t - Translation function from i18next
 * @returns Localized duration string (e.g., "3 days and 1h30" or "3 giorni e 1h30")
 */
export const formatDurationFromMinutes = (
    minutes: number,
    t: TFunction,
): string => {
    if (minutes < 0) {
        return '';
    }

    if (minutes < 1) {
        return t(FALLBACK_MINUTE_TEXT, { count: 0 });
    }

    const totalMinutes = Math.floor(minutes);
    const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
    const hours = Math.floor(
        (totalMinutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR,
    );
    const remainingMinutes = totalMinutes % MINUTES_PER_HOUR;

    const parts: string[] = [];

    // Add days
    if (days > 0) {
        parts.push(
            t(`duration.day_${days === 1 ? 'one' : 'other'}`, { count: days }),
        );
    }

    // Add hours
    if (hours > 0) {
        parts.push(
            t(`duration.hour_${hours === 1 ? 'one' : 'other'}`, {
                count: hours,
            }),
        );
    }

    // Add minutes (always show if > 0 when we have hours, or if no days/hours)
    if (remainingMinutes > 0 && days === 0) {
        parts.push(
            t(`duration.minute_${remainingMinutes === 1 ? 'one' : 'other'}`, {
                count: remainingMinutes,
            }),
        );
    }

    return formatDurationParts(parts, t);
};

/**
 * Converts minutes to a compact duration string (e.g., "3d 1h 30m")
 *
 * @param minutes - Number of minutes to convert
 * @returns Compact duration string
 */
export const formatDurationCompact = (minutes: number): string => {
    if (minutes < 0) {
        return '';
    }

    if (minutes < 1) {
        return '0m';
    }

    const totalMinutes = Math.floor(minutes);
    const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
    const hours = Math.floor(
        (totalMinutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR,
    );
    const remainingMinutes = totalMinutes % MINUTES_PER_HOUR;

    const parts: string[] = [];

    if (days > 0) {
        parts.push(`${days}d`);
    }

    if (hours > 0) {
        parts.push(`${hours}h`);
    }

    if (remainingMinutes > 0) {
        parts.push(`${remainingMinutes}m`);
    }

    return parts.length > 0 ? parts.join(' ') : '0m';
};
